import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_TRAINING_BY_ID, GET_TRAINING_IMAGE } from "@/lib/graphql/queries";
import { Button } from "@/components/ui/button";
import ProviderLogo from "@/components/common/ProviderLogo";
import SocialShareMenu from "@/components/sharing/SocialShareMenu";
import OpenGraphTags from "@/components/SEO/OpenGraphTags";
import {
  Calendar,
  Clock,
  Globe,
  Award,
  Users,
  Star,
  ArrowLeft,
  MapPin,
  BookOpen,
  Beaker,
  FileText,
  ImageIcon,

  CheckCircle,
  Info,
  Heart,

  ExternalLink,
  XCircle,
  Building,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import { enrollInTraining } from "@/features/training/trainingSlice";
import { RootState } from "@/store";
import { Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Enhanced TrainingImage component with loading states and fallbacks
const TrainingImage = ({ filename, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { loading, error, data } = useQuery(GET_TRAINING_IMAGE, {
    variables: { filename },
    skip: !filename || filename.startsWith("http"),
  });

  if (loading) return <Skeleton className={`${className} rounded-xl shadow-lg`} />;
  if (error || !data) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 rounded-xl`}>
        <ImageIcon className="text-gray-400 h-12 w-12" />
      </div>
    );
  }

  const src = filename.startsWith("http")
    ? filename
    : `data:${data.getTrainingImageByName?.mimetype};base64,${data.getTrainingImageByName?.base64Data}`;

  return (
    <div className="relative">
      {!isLoaded && <Skeleton className={`${className} rounded-xl absolute inset-0`} />}
      <motion.img
        src={src}
        alt={alt}
        className={`${className} rounded-xl object-cover shadow-lg ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        loading="lazy"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

const TrainingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("modules");
  const contentRef = useRef(null);

  const { enrollmentError, enrollments, enrollmentLoading } = useSelector(
    (state: RootState) => state.training
  );

  const { loading, error, data } = useQuery(GET_TRAINING_BY_ID, {
    variables: { id: parseInt(id || "0", 10) },
    skip: !id,
  });

  // Check if user is already enrolled
  useEffect(() => {
    if (data?.getTrainingById && user) {
      const training = data.getTrainingById;
      const userId = String(user.id);
      const enrolledIds = training.enrolledUserIds?.map(id => String(id)) || [];
      const userEnrolledInTraining = enrolledIds.includes(userId);
      const userEnrolledInRedux = enrollments?.some(
        enrollment => String(enrollment.trainingId) === String(id) &&
                     String(enrollment.userId) === userId
      );
      setIsEnrolled(userEnrolledInTraining || userEnrolledInRedux);
    }
  }, [data, user, enrollments, id]);

  // Scroll to content section when tab changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeTab]);

  const handleSave = () => {
    setIsSaved(!isSaved);
    toast({
      title: isSaved ? "Training Removed" : "Training Saved",
      description: isSaved ? "Removed from your collection" : "Added to your learning collection",
      variant: "default"
    });
  };

  // Share functionality is now handled by the SocialShareMenu component

  const handleEnroll = async () => {
    if (isEnrolled) {
      toast({
        title: "Already Enrolled",
        description: "You are already registered for this training"
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to enroll in this training",
        action: (
          <Button variant="default" size="sm" onClick={() => navigate('/login', { state: { from: `/training/${id}` } })}>
            Log In
          </Button>
        )
      });
      return;
    }

    try {
      await dispatch(enrollInTraining(parseInt(id || "0", 10)) as any);
      toast({
        title: "Enrollment Successful",
        description: "You have been registered for this training",
        variant: "default"
      });
      setIsEnrolled(true);
    } catch (err) {
      toast({
        title: "Enrollment Failed",
        description: enrollmentError || "An error occurred during enrollment",
        variant: "destructive"
      });
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorState error={error} />;
  if (!data?.getTrainingById) return <NotFoundState />;

  const training = data.getTrainingById;
  const startDate = new Date(training.startDate);
  const formattedDate = startDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const today = new Date();
  const daysUntilStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const isUpcoming = daysUntilStart > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO and Open Graph Tags for social sharing */}
      <OpenGraphTags
        title={training?.title || 'Training Details'}
        description={training?.description?.substring(0, 200) || 'Explore this training course and enhance your skills'}
        url={window.location.href}
        imageUrl={training?.image || ''}
        type="article"
      />
      {/* Sticky Header with Progress Indicator */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-4 border-b shadow-sm"
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Button asChild variant="ghost" size="sm" className="group">
            <Link to="/courses">
              <ArrowLeft className="mr-2 h-5 w-5 transition-transform group-hover:-translate-x-1" />
              Back to Trainings
            </Link>
          </Button>
          <div className="flex gap-3">
            <SocialShareMenu
              url={window.location.href}
              title={training?.title || 'Check out this training'}
              description={training?.description || 'I found this interesting training you might like'}
              imageUrl={training?.image || ''}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isSaved ? "default" : "outline"}
                    size="sm"
                    onClick={handleSave}
                    className={isSaved ? "bg-primary/20 hover:bg-primary/30 text-primary border-primary" : ""}
                  >
                    {isSaved ? (
                      <Heart className="mr-2 h-4 w-4 fill-primary text-primary" />
                    ) : (
                      <Heart className="mr-2 h-4 w-4" />
                    )}
                    {isSaved ? "Saved" : "Save"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isSaved ? "Remove from saved" : "Save to your collection"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </motion.div>

      {/* Hero Banner with Image Background */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          {training.image && (
            <TrainingImage
              filename={training.image}
              alt={training.title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-indigo-900/80"></div>
        </div>
        <div className="container relative z-10 mx-auto px-4 py-16 md:py-24 max-w-7xl">
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="secondary" className="bg-white/20 text-white border-none hover:bg-white/30">{training.category}</Badge>
            <Badge variant="secondary" className="bg-white/20 text-white border-none hover:bg-white/30">{training.level}</Badge>
            {isUpcoming && (
              <Badge className="bg-amber-500/80 text-white border-none">
                Starts in {daysUntilStart} {daysUntilStart === 1 ? 'day' : 'days'}
              </Badge>
            )}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 max-w-4xl">{training.title}</h1>
          <div className="flex flex-wrap items-center gap-6 text-sm mb-8">
            <div className="flex items-center bg-white/10 rounded-full px-4 py-2">
              <Star className="h-5 w-5 text-yellow-300 mr-2" />
              <span className="font-medium">{training.rating || 0}/5</span>
              <span className="text-white/80 ml-2">({training.reviews || 0} reviews)</span>
            </div>
            <div className="flex items-center bg-white/10 rounded-full px-4 py-2">
              <Users className="h-5 w-5 text-white/80 mr-2" />
              <span>{training.enrollments || 0} registrations</span>
            </div>
            <div className="flex items-center bg-white/10 rounded-full px-4 py-2">
              <Calendar className="h-5 w-5 text-white/80 mr-2" />
              <span>{formattedDate}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
              <div className="bg-white/20 rounded-full p-3 mr-4">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Duration</p>
                <p className="font-semibold">{training.duration}</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
              <div className="bg-white/20 rounded-full p-3 mr-4">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Language</p>
                <p className="font-semibold">{training.language}</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
              <div className="bg-white/20 rounded-full p-3 mr-4">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Format</p>
                <p className="font-semibold capitalize">{training.format}</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
              <div className="bg-white/20 rounded-full p-3 mr-4">
                <Award className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Certificate</p>
                <p className="font-semibold">{training.certificate ? "Included" : "Not included"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-8 space-y-8"
          >
            <Card className="shadow-md overflow-hidden border-none">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent pb-3">
                <CardTitle className="text-2xl flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full mr-3">
                    <Info className="h-5 w-5 text-primary" />
                  </div>
                  About This Training
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-muted-foreground leading-relaxed text-base whitespace-pre-wrap">{training.description}</div>
              </CardContent>
              {training.highlights && (
                <div className="bg-muted/10 p-6 border-t">
                  <h3 className="font-medium mb-4 flex items-center text-lg">
                    <Info className="h-5 w-5 mr-2 text-primary" />
                    Key Highlights
                  </h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {training.highlights.map((highlight: string, index: number) => (
                      <li key={index} className="flex items-start gap-3 bg-primary/5 p-3 rounded-lg">
                        <div className="bg-primary/20 rounded-full p-1 mt-1">
                          <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                        </div>
                        <span className="text-sm">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
            <Card className="shadow-md overflow-hidden border-none">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent">
                <CardTitle className="text-xl flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full mr-3">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  Meet Your Instructor
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                  <div className="bg-gradient-to-br from-primary to-primary/70 rounded-full w-24 h-24 flex-shrink-0 flex items-center justify-center text-white font-bold text-3xl shadow-md">
                    {training.instructor.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-xl mb-1 text-center md:text-left">{training.instructor.name}</h3>
                    <p className="text-primary font-medium mb-3 text-center md:text-left">{training.instructor.specialization}</p>
                    {training.instructor.bio && (
                      <p className="text-muted-foreground leading-relaxed">{training.instructor.bio}</p>
                    )}
                    {training.instructor.credentials && (
                      <div className="mt-4 p-3 bg-muted/10 rounded-lg border border-muted/20">
                        <p className="text-sm">
                          <span className="font-medium text-foreground">Credentials: </span>
                          {training.instructor.credentials}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            {training.requirements?.length > 0 && (
              <Card className="shadow-md border-none">
                <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent">
                  <CardTitle className="text-xl flex items-center">
                    <div className="bg-primary/10 p-2 rounded-full mr-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ul className="space-y-4">
                    {training.requirements.map((req: string, index: number) => (
                      <li key={index} className="flex items-start gap-4 p-3 bg-muted/10 rounded-lg hover:bg-muted/20 transition-colors">
                        <div className="bg-primary/10 rounded-full p-2 mt-1 flex-shrink-0">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                        </div>
                        <span className="text-muted-foreground">{req}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-4 space-y-6"
          >
            <Card className="shadow-lg border-none overflow-hidden sticky top-24">
              <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Price</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-primary">{training.price}</span>
                      <span className="text-lg font-medium">TND</span>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-sm px-3 py-1.5 capitalize bg-white/80 text-primary">{training.format}</Badge>
                </div>
              </div>
              <CardContent className="p-6 space-y-6">
                <AnimatePresence mode="wait">
                  {isEnrolled ? (
                    <motion.div
                      key="enrolled"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <Button className="w-full gap-2 bg-green-600 hover:bg-green-700 py-6 text-base" size="lg" disabled>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Already Registered
                      </Button>
                      <p className="text-center text-sm mt-3 text-muted-foreground">
                        You're all set for this training!
                      </p>
                    </motion.div>
                  ) : enrollmentLoading ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <Button className="w-full gap-2 py-6 text-base" size="lg" disabled>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Enrolling...
                      </Button>
                      <Progress className="h-1.5 mt-3" value={65} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="register"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Button
                        className="w-full gap-2 text-base py-6 font-semibold"
                        size="lg"
                        onClick={handleEnroll}
                      >
                        Register Now
                      </Button>
                      {isUpcoming && (
                        <p className="text-center text-sm mt-3 text-amber-600 font-medium">
                          Limited spots available
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="grid grid-cols-1 gap-4 text-sm mt-6">
                  {[
                    { icon: Clock, label: "Duration", value: training.duration },
                    { icon: Calendar, label: "Starts", value: formattedDate },
                    { icon: Globe, label: "Language", value: training.language },
                    ...(training.certificate ? [{ icon: Award, label: "Certificate", value: "Included" }] : []),
                    ...(training.location ? [{ icon: MapPin, label: "Location", value: training.location }] : []),
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-muted/10 rounded-lg">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <item.icon className="h-4 w-4 text-primary flex-shrink-0" />
                      </div>
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-muted-foreground">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {training.providerEntity && (
                  <motion.div
                    className="mt-6 rounded-lg overflow-hidden border shadow-md hover:shadow-lg transition-all duration-300"
                    whileHover={{ y: -2 }}
                  >
                    <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-4 border-b">
                      <h3 className="font-medium flex items-center gap-2">
                        <Building className="h-4 w-4 text-primary" />
                        Training Provider
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-4 mb-4">
                        {training.providerEntity.logoUrl ? (
                          <div className="relative group">
                            <div className="absolute inset-0 bg-primary/10 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <ProviderLogo
                              filename={training.providerEntity.logoUrl}
                              alt={training.providerEntity.name || 'Provider'}
                              className="h-16 w-16 rounded-lg object-contain bg-white p-1 border shadow-sm relative z-10 transition-transform group-hover:scale-105"
                            />
                          </div>
                        ) : (
                          <div className="h-16 w-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg flex items-center justify-center shadow-sm border group transition-transform hover:scale-105">
                            <span className="font-bold text-primary text-2xl group-hover:scale-110 transition-transform">
                              {training.providerEntity.name ? training.providerEntity.name.charAt(0) : 'P'}
                            </span>
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{training.providerEntity.name || 'Provider'}</h4>
                          {training.providerEntity.type && (
                            <Badge variant="outline" className="mt-1 bg-primary/5 text-primary border-primary/20">
                              {training.providerEntity.type}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {training.providerEntity.description && (
                        <div className="mb-4 p-3 bg-muted/5 rounded-lg border text-sm text-muted-foreground">
                          <p className="line-clamp-2">{training.providerEntity.description}</p>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-2 mt-4">
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1 bg-primary/90 hover:bg-primary transition-colors gap-2"
                          asChild
                        >
                          <Link to={`/providers/${training.providerEntity.id}`} className="flex items-center justify-center">
                            <Info className="h-4 w-4" />
                            View Provider Profile
                          </Link>
                        </Button>

                        {training.provider.website && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 hover:bg-primary/5 hover:text-primary transition-colors gap-2"
                            asChild
                          >
                            <a href={training.provider.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                              <ExternalLink className="h-4 w-4" />
                              Visit Website
                            </a>
                          </Button>
                        )}
                      </div>

                      {/* Provider stats if available */}
                      {(training.providerEntity.trainingsCount || training.providerEntity.rating) && (
                        <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                          {training.providerEntity.trainingsCount && (
                            <div className="flex items-center gap-2 p-2 bg-muted/5 rounded-lg border">
                              <BookOpen className="h-4 w-4 text-primary" />
                              <div>
                                <span className="font-medium">{training.providerEntity.trainingsCount}</span>
                                <span className="text-muted-foreground"> Trainings</span>
                              </div>
                            </div>
                          )}
                          {training.providerEntity.rating && (
                            <div className="flex items-center gap-2 p-2 bg-muted/5 rounded-lg border">
                              <Star className="h-4 w-4 text-amber-500" />
                              <div>
                                <span className="font-medium">{training.providerEntity.rating}</span>
                                <span className="text-muted-foreground"> Rating</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-12"
          ref={contentRef}
        >
          <h2 className="text-2xl font-bold mb-6">Training Content</h2>
          <Tabs
            defaultValue="modules"
            className="w-full"
            onValueChange={setActiveTab}
            value={activeTab}
          >
            <TabsList className="grid w-full grid-cols-3 mb-8 p-1 bg-muted rounded-xl">
              {[
                { value: "modules", icon: BookOpen, label: `Modules (${training.modules?.length || 0})` },
                { value: "labs", icon: Beaker, label: `Labs (${training.labs?.length || 0})` },
                { value: "resources", icon: FileText, label: `Resources (${training.resources?.length || 0})` },
              ].map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex items-center gap-2 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg transition-all"
                >
                  <tab.icon className="h-5 w-5" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <TabsContent value="modules" className="mt-0">
                  <ContentSection
                    items={training.modules}
                    icon={BookOpen}
                    emptyMessage="No modules available"
                    type="module"
                  />
                </TabsContent>
                <TabsContent value="labs" className="mt-0">
                  <ContentSection
                    items={training.labs}
                    icon={Beaker}
                    emptyMessage="No labs available"
                    type="lab"
                  />
                </TabsContent>
                <TabsContent value="resources" className="mt-0">
                  <ContentSection
                    items={training.resources}
                    icon={FileText}
                    emptyMessage="No resources available"
                    type="resource"
                  />
                </TabsContent>
              </motion.div>
            </AnimatePresence>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

// ContentSection, LoadingSkeleton, ErrorState, and NotFoundState components remain unchanged
const ContentSection = ({ items, icon: Icon, emptyMessage, type }) => (
  items?.length > 0 ? (
    <div className="space-y-4">
      {items.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          whileHover={{ y: -2 }}
          className="transition-all"
        >
          <Card className="shadow-md hover:shadow-lg transition-shadow border border-transparent hover:border-primary/10">
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <div className="bg-primary/10 rounded-full p-2">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg">{item.title || item.name}</CardTitle>
                {item.duration && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center">
                    <Clock className="h-3 w-3 mr-1" /> {item.duration}
                  </p>
                )}
              </div>
              {type === "resource" && item.url && (
                <Button variant="ghost" size="sm" className="ml-auto" asChild>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="text-muted-foreground text-sm whitespace-pre-wrap">{item.description}</div>
              {item.topics && item.topics.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium mb-2">Topics covered:</p>
                  <div className="flex flex-wrap gap-2">
                    {item.topics.map((topic, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-muted">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  ) : (
    <Card className="shadow-md">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Icon className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
        <p className="text-muted-foreground">{emptyMessage}</p>
      </CardContent>
    </Card>
  )
);

const LoadingSkeleton = () => (
  <div className="container mx-auto px-4 py-12 max-w-7xl">
    <Skeleton className="h-10 w-32 mb-8" />
    <div className="mb-12">
      <Skeleton className="h-6 w-24 mb-3" />
      <Skeleton className="h-12 w-3/4 mb-4" />
      <div className="flex gap-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-32" />
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 space-y-6">
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
      <div className="lg:col-span-5 space-y-6">
        <Skeleton className="h-80 w-full rounded-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    </div>
    <div className="mt-12">
      <Skeleton className="h-10 w-48 mb-6" />
      <Skeleton className="h-12 w-full mb-8 rounded-xl" />
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    </div>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="container mx-auto px-4 py-12 max-w-7xl">
    <Button asChild variant="ghost" size="sm" className="mb-8">
      <Link to="/courses">
        <ArrowLeft className="mr-2 h-5 w-5" /> Back to Trainings
      </Link>
    </Button>
    <Card className="shadow-lg border-l-4 border-l-destructive max-w-2xl mx-auto">
      <CardContent className="pt-6 text-center py-12">
        <div className="bg-destructive/10 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <XCircle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-xl font-semibold text-destructive mb-2">Error Loading Training</h3>
        <p className="text-muted-foreground mb-6">{error.message}</p>
        <Button asChild variant="outline">
          <Link to="/courses">View All Trainings</Link>
        </Button>
      </CardContent>
    </Card>
  </div>
);

const NotFoundState = () => (
  <div className="container mx-auto px-4 py-12 max-w-7xl">
    <Card className="shadow-lg">
      <CardContent className="text-center py-12">
        <h3 className="text-2xl font-semibold mb-2">Training Not Found</h3>
        <p className="text-muted-foreground">This training may have been removed or doesn’t exist.</p>
      </CardContent>
    </Card>
  </div>
);

export default TrainingDetails;